import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useMembershipMode, MembershipMode, MemberRole } from "@/hooks/useMembershipMode";

interface MembershipSettingsProps {
  organizationId?: string;
}

export const MembershipSettings = ({ organizationId }: MembershipSettingsProps = {}) => {
  const {
    settings,
    loading,
    updateMembershipMode,
    updateDefaultMemberRole,
    updateAutoApproveDomains,
    updateRegistrationMessage,
  } = useMembershipMode(organizationId);

  const [newDomain, setNewDomain] = useState('');

  const handleModeChange = (mode: MembershipMode) => {
    updateMembershipMode(mode);
  };

  const handleRoleChange = (role: MemberRole) => {
    updateDefaultMemberRole(role);
  };

  const handleAddDomain = () => {
    if (newDomain.trim() && !settings.autoApproveDomains.includes(newDomain.trim())) {
      const updatedDomains = [...settings.autoApproveDomains, newDomain.trim()];
      updateAutoApproveDomains(updatedDomains);
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    const updatedDomains = settings.autoApproveDomains.filter(d => d !== domain);
    updateAutoApproveDomains(updatedDomains);
  };

  const handleMessageChange = (message: string) => {
    updateRegistrationMessage(message);
  };

  if (loading) {
    return <div>Loading membership settings...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Membership Settings</CardTitle>
        <CardDescription>
          Control how new members can join your organization
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Membership Mode */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Membership Mode</Label>
          <RadioGroup value={settings.membershipMode} onValueChange={handleModeChange}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="open" id="open" />
              <Label htmlFor="open" className="font-normal">
                <div>
                  <div className="font-medium">Open Registration</div>
                  <div className="text-sm text-muted-foreground">
                    Anyone can sign up and join immediately
                  </div>
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="approval_required" id="approval" />
              <Label htmlFor="approval" className="font-normal">
                <div>
                  <div className="font-medium">Approval Required</div>
                  <div className="text-sm text-muted-foreground">
                    Users can register but need admin approval
                  </div>
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="invite_only" id="invite" />
              <Label htmlFor="invite" className="font-normal">
                <div>
                  <div className="font-medium">Invite Only</div>
                  <div className="text-sm text-muted-foreground">
                    Only admins can invite new members
                  </div>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Default Member Role */}
        <div className="space-y-3">
          <Label htmlFor="default-role" className="text-base font-medium">
            Default Member Role
          </Label>
          <Select value={settings.defaultMemberRole} onValueChange={handleRoleChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select default role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="guest">Guest</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            The role automatically assigned to new members
          </p>
        </div>

        {/* Auto-approve Domains (only for approval_required mode) */}
        {settings.membershipMode === 'approval_required' && (
          <div className="space-y-3">
            <Label className="text-base font-medium">Auto-approve Domains</Label>
            <div className="space-y-2">
              <div className="flex space-x-2">
                <Input
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
                />
                <Button onClick={handleAddDomain} variant="outline" size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {settings.autoApproveDomains.map((domain) => (
                  <Badge key={domain} variant="secondary" className="flex items-center gap-1">
                    {domain}
                    <button
                      onClick={() => handleRemoveDomain(domain)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                Users with email addresses from these domains will be automatically approved
              </p>
            </div>
          </div>
        )}

        {/* Registration Message */}
        <div className="space-y-3">
          <Label htmlFor="registration-message" className="text-base font-medium">
            Registration Message
          </Label>
          <Textarea
            id="registration-message"
            placeholder="Optional message shown to users during registration..."
            value={settings.registrationMessage}
            onChange={(e) => handleMessageChange(e.target.value)}
            rows={3}
          />
          <p className="text-sm text-muted-foreground">
            Custom message displayed to users when they attempt to register
          </p>
        </div>

        {/* Current Mode Summary */}
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">Current Configuration</h4>
          <div className="text-sm space-y-1">
            <div>
              <span className="font-medium">Mode:</span>{' '}
              <Badge variant="outline">
                {settings.membershipMode.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            <div>
              <span className="font-medium">Default Role:</span> {settings.defaultMemberRole}
            </div>
            {settings.autoApproveDomains.length > 0 && (
              <div>
                <span className="font-medium">Auto-approve:</span> {settings.autoApproveDomains.length} domains
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};